import { useState } from "react";
import { X, Crown, BookOpen, FileText, CheckCircle, Sparkles, Lock, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function PremiumPopup({ user, onClose, onUpgrade }) {
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("premium");

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    setLoading(true);
    
    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Failed to load payment gateway");
        setLoading(false);
        return;
      }

      // Create order
      const orderResponse = await axios.post(`${API}/payment/create-order`, {
        user_id: user.id,
        plan_id: selectedPlan
      });
      const order = orderResponse.data;

      // Razorpay options
      const options = {
        key: order.key,
        amount: order.amount,
        currency: order.currency,
        name: "Descriptive Test",
        description: "Premium Plan - Full Access",
        order_id: order.orderId,
        handler: async function (response) {
          try {
            // Verify payment
            const verifyResponse = await axios.post(`${API}/payment/verify`, {
              user_id: user.id,
              plan_id: selectedPlan,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            if (verifyResponse.data.success) {
              toast.success("Payment successful! Welcome to Premium!");
              if (onUpgrade) onUpgrade(verifyResponse.data.user);
              onClose();
            }
          } catch (error) {
            toast.error(error.response?.data?.detail || "Payment verification failed");
          }
        },
        prefill: {
          name: user.name,
          email: user.email,
          contact: user.mobile || ""
        },
        theme: {
          color: "#8b5cf6"
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
          }
        }
      };

      // Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      setLoading(false);
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(error.response?.data?.detail || error.message || "Payment initiation failed");
      setLoading(false);
    }
  };

  const features = [
    { icon: BookOpen, text: "All Notes - Every Subject & Topic", highlight: true },
    { icon: FileText, text: "Unlimited MCQ Practice", highlight: true },
    { icon: CheckCircle, text: "300 Descriptive Answer Evaluations" },
    { icon: Sparkles, text: "AI-Powered Feedback & Scoring" },
    { icon: Crown, text: "Priority Support" }
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="premium-popup">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 p-6 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
          
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            data-testid="close-premium-popup"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="relative">
            <div className="flex items-center gap-2 mb-2">
              <Lock className="w-5 h-5" />
              <span className="text-sm font-medium text-white/90">Content Locked</span>
            </div>
            <h2 className="text-2xl font-bold mb-1">Unlock Full Access</h2>
            <p className="text-white/80 text-sm">Get unlimited access to all notes & MCQs</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Price Card */}
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-4 mb-5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-purple-600" />
                <span className="font-bold text-gray-800">Premium Plan</span>
              </div>
              <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full">BEST VALUE</span>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-black text-purple-700">₹499</span>
              <span className="text-gray-500 line-through text-sm">₹599</span>
              <span className="text-green-600 text-sm font-semibold ml-2">50% OFF</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">One-time payment • Lifetime access to notes</p>
          </div>

          {/* Features */}
          <div className="space-y-3 mb-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">What you get:</p>
            {features.map((feature, idx) => (
              <div key={idx} className={`flex items-center gap-3 ${feature.highlight ? "text-purple-700 font-medium" : "text-gray-600"}`}>
                <feature.icon className={`w-4 h-4 ${feature.highlight ? "text-purple-600" : "text-gray-400"}`} />
                <span className="text-sm">{feature.text}</span>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <Button
            onClick={handlePayment}
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold py-6 text-lg rounded-xl shadow-lg shadow-purple-500/30 transition-all hover:shadow-xl hover:shadow-purple-500/40"
            data-testid="pay-now-button"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Processing...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Pay ₹499 & Unlock Now
              </span>
            )}
          </Button>

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-4 mt-4 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Secure Payment
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Instant Access
            </span>
          </div>
          
          <p className="text-center text-xs text-gray-400 mt-3">
            Powered by Razorpay • 100% Secure
          </p>
        </div>
      </div>
    </div>
  );
}
